import React from "react";
import { Provider } from "react-redux";
import { createRoot, Root } from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { store } from "../../../app/store";
import SingleProductContainer from "../components/productSingle/SingleProductContainer";

// Store created roots by their container ID
const roots: Record<string, Root> = {};

/**
 * Initialize the SingleProductContainer component
 * @param rootId - ID of the DOM element to mount the component
 */
export function hydrateSingleProductContainer(rootId: string): void {
  // Find the root element
  const rootElement = document.getElementById(rootId);
  if (!rootElement) {
    console.error(`Failed to find root element with ID: ${rootId}`);
    return;
  }

  // Check if this container has already been initialized
  if (rootElement.dataset.reactInitialized === "true") {
    console.log(`Container ${rootId} is already initialized`);
    return;
  }

  try {
    // Check if we already have a root for this element
    if (roots[rootId]) {
      // Use the existing root instance
      console.log(`Updating existing React root for ${rootId}`);
      roots[rootId].render(
        <React.StrictMode>
          <Provider store={store}>
            <BrowserRouter>
              <Routes>
                <Route
                  path="/shop/:productSlug"
                  element={<SingleProductContainer />}
                />
                <Route
                  path="/product/:productSlug"
                  element={<SingleProductContainer />}
                />
              </Routes>
            </BrowserRouter>
          </Provider>
        </React.StrictMode>
      );
    } else {
      // Create new root and store it
      console.log(`Creating new React root for ${rootId}`);
      const root = createRoot(rootElement);
      roots[rootId] = root;
      root.render(
        <React.StrictMode>
          <Provider store={store}>
            <BrowserRouter>
              <Routes>
                <Route
                  path="/shop/:productSlug"
                  element={<SingleProductContainer />}
                />
                <Route
                  path="/product/:productSlug"
                  element={<SingleProductContainer />}
                />
              </Routes>
            </BrowserRouter>
          </Provider>
        </React.StrictMode>
      );
    }

    // Mark container as initialized
    rootElement.dataset.reactInitialized = "true";
    console.log("SingleProductContainer component successfully initialized");
  } catch (error) {
    console.error(
      "Error initializing SingleProductContainer component:",
      error
    );
  }
}

// Declare global type for the hydration function
declare global {
  interface Window {
    hydrateSingleProductContainer?: (rootId: string) => void;
  }
}

// Export the hydration function for use in the global scope
if (typeof window !== "undefined") {
  window.hydrateSingleProductContainer = hydrateSingleProductContainer;
}
